import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useCanceledPairs } from '@trello/business-logic-react/action';
import { sendErrorEvent } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { optimisticIdManager } from '@trello/graphql';
import { BoardIdProvider, WorkspaceIdProvider } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { usePreviousWhileLoading } from '@trello/workspaces';

import { Action, ActionsEmpty } from 'app/src/components/Action';
import { WorkspacePageSpinner } from 'app/src/components/WorkspacePages/WorkspacePageSpinner';
import {
  useMemberActivityListLazyQuery,
  useMemberActivityListQuery,
  type MemberActivityListQuery,
} from './MemberActivityListQuery.generated';

interface MemberActivityListProps {
  memberId: string;
}

export const MemberActivityList = ({ memberId }: MemberActivityListProps) => {
  const [page, setPage] = useState(0);
  const resultLimit = 10;
  const [loadMoreActions] = useMemberActivityListLazyQuery();

  const { loading, data } = useMemberActivityListQuery({
    variables: {
      memberId,
      limit: resultLimit,
      page,
    },
    waitOn: ['None'],
    // eslint-disable-next-line @trello/disallow-fetch-policies
    fetchPolicy: 'network-only',
  });

  const allActions: MemberActivityListQuery['memberActions'] =
    usePreviousWhileLoading(data?.memberActions ?? [], loading, []);

  const retrieveNextPage = useCallback(() => {
    loadMoreActions({
      variables: {
        memberId,
        limit: resultLimit,
        page: page + 1,
      },
    })
      .then(() => {
        setPage(page + 1);
      })
      .catch((error) => {
        showFlag({
          id: 'memberActivityListLoadMoreError',
          title: 'Error loading activity',
          appearance: 'error',
        });
        sendErrorEvent(error, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Member Activity',
          },
        });
      });
  }, [loadMoreActions, memberId, page]);

  const actions = useCanceledPairs(allActions);

  const { value: isLoaderGateEnabled } = useFeatureGate(
    'trello_web_member_activity_loader',
  );

  if (loading && (!isLoaderGateEnabled || actions.length === 0)) {
    return <WorkspacePageSpinner />;
  }

  if (actions.length === 0) {
    return <ActionsEmpty daysBack={30} />;
  }
  return (
    <ul>
      {actions.map((action) => {
        const dataJson = JSON.parse(action.data);

        const boardIdContext = dataJson.board?.id ?? '';
        const workspaceIdContext = dataJson.organization?.id ?? '';

        return (
          <li key={optimisticIdManager.getStableIdKey(action.id)}>
            <WorkspaceIdProvider value={workspaceIdContext} key={action.id}>
              <BoardIdProvider value={boardIdContext}>
                <Action
                  key={optimisticIdManager.getStableIdKey(action.id)}
                  id={action.id}
                  location="member"
                  memberId={memberId}
                  date={action.date}
                  translationKey={action.display.translationKey}
                  entities={action.display.entities}
                  reactions={[]}
                  boardName={dataJson.board?.name}
                />
              </BoardIdProvider>
            </WorkspaceIdProvider>
          </li>
        );
      })}
      <Button onClick={retrieveNextPage} isLoading={loading}>
        <FormattedMessage
          id="templates.board_menu_activity.load-more-activity"
          defaultMessage="Load more activity"
          description="Button to load more activity on a board"
        />
      </Button>
    </ul>
  );
};
